import { FilterType, Page, Service } from './page-types';
import { config } from 'app/config/config';
import { getDKPartners, Partner } from 'app/utils/partner-utils';
import { CrmSystem, CustomerType } from 'app/store/types/user-context-types';
import { GtmElementCategory } from 'app/utils/gtm-tracking-utils';
import { partner } from 'app/mocks/mock-data/partner';

const CONTEXT_PATH: string = config.app.contextPath;
export interface Pages {
  aria: Page;
  ariaTv: Page;
  enterpriseDDOS: Page;
  enterpriseExtendedSecurity: Page;
  enterpriseFirewall: Page;
  enterpriseGuestWifi: Page;
  enterpriseLineOverview: Page;
  enterpriseLineStatus: Page;
  enterpriseMyProducts: Page;
  enterpriseInternetSettingsOverview: Page;
  enterpriseInternetSettingsNetworkSettings: Page;
  enterpriseWifiSettings: Page;
  enterpriseUpsale: Page;
  myHgw: Page;
  myUsageDK: Page;
  meshNetworkStoreDK: Page;
  invoiceInformation: Page;
  voipDK: Page;
  orderDK: Page;
  account: Page;
  authCallback: Page;
  changePin: Page;
  deliveryStatus: Page;
  email: Page;
  emailCreateNew: Page;
  emailChangeSenderName: Page;
  emailChangePassword: Page;
  emailDeleteAccount: Page;
  emailActivateAccount: Page;
  hgwUserManual: Page;
  home: Page;
  internet: Page;
  internetHjemApp: Page;
  internetSecurity: Page;
  internetSecurityFilter: Page;
  securityInsurancePackage: Page;
  internetSettingsOverview: Page;
  internetSettingsOverviewDk: Page;
  internetSettingsNetworkSettings: Page;
  internetSettingsNetworkSettingsDk: Page;
  internetSettingsPortForwarding: Page;
  internetSettingsPortForwardingDk: Page;
  internetSettingsWifiBand: Page;
  internetSettingsWifiAdvanced: Page;
  invoice: Page;
  invoiceDetails: Page;
  invoicePostpone: Page;
  invoiceAvtaleGiro: Page;
  invoiceAvtaleGiroReceipt: Page;
  invoiceLegacy: Page;
  invoiceLyse: Page;
  logout: Page;
  myProducts: Page;
  myProductsDkEnterprise: Page;
  orderTrackerOpenNet: Page;
  privacyAndConsents: Page;
  provAuthCallback: Page;
  streamingServices: Page;
  streamingService: Page;
  telephone: Page;
  tv: Page;
  tvStoreOpenNet: Page;
  users: Page;
  userEdit: Page;
  userCreate: Page;
  userDelete: Page;
  contactDetails: Page;
  contactDetailsEdit: Page;
  generalMarketing: Page;
  reservations: Page;
  consentLyse: Page;
  internetSettingsWifiBridgeMode: Page;
  firewallCreateRule: Page;
  firewallUpdateRule: Page;
  foc: Page;
  inactiveMovies: Page;
  ispDevices: Page;
  modellAStore: Page;
  movieRentalHistory: Page;
  myDevices: Page;
  newsAndOffers: Page;
  relocation: Page;
  relocationFormVikenFiber: Page;
  relocationFormLyse: Page;
  segmentPicker: Page;
  meshNetworkStore: Page;
  tvStore: Page;
  voipOldForCustomerService: Page;
  voipSettingsOverview: Page;
  voipSettingsForwarding: Page;
  voipSettingsForwardingNewRule: Page;
  voipSettingsRejectionNewRule: Page;
  voipSettingsCalls: Page;
  voipSettingsBarring: Page;
  voipUsage: Page;
}

const commonNorwegianPartnerOnlyVisiblity = {
  partners: [...getDKPartners(), Partner.NO_EidsivaBredbaand],
  filterType: FilterType.Blacklist,
};

export const commonDanishPartnerOnlyVisibility = {
  filterType: FilterType.Whitelist,
  partners: getDKPartners(),
};

const emailCommonRequirements = {
  partnerVisibility: {
    filterType: FilterType.Blacklist,
    partners: [Partner.NO_EidsivaBredbaand],
  },
};

const commonInvoiceRequirements = {
  partnerVisibility: {
    partners: [
      Partner.NO_EidsivaBredbaand,
      Partner.NO_Lyse,
      Partner.NO_KleppEnergi,
      Partner.NO_FinnaasKraftlag,
      Partner.NO_Stroeyma,
    ],
    filterType: FilterType.Blacklist,
  },
  crm: CrmSystem.INFONOVA,
};

const internetSecurityVisibility = {
  filterType: FilterType.Whitelist,
  partners: [
    Partner.NO_Afiber,
    Partner.NO_Altibox_Training_Tenant,
    Partner.NO_Altifiber,
    Partner.NO_BergenFiber,
    Partner.NO_Bruse,
    Partner.NO_Drangedal,
    Partner.NO_EidsivaBredbaandAS,
    Partner.NO_Enivest,
    Partner.NO_FinnaasKraftlag,
    Partner.NO_FitjarKraftlag,
    Partner.NO_HaugalandKraft,
    Partner.NO_IstadFiber,
    Partner.NO_KleppEnergi,
    Partner.NO_KrageroeBredbaand,
    Partner.NO_LofotkraftBredbaand,
    Partner.NO_Lyse,
    Partner.NO_MidtTelemarkBreiband,
    Partner.NO_NotoddenEnergi,
    Partner.NO_NTE,
    Partner.NO_SandefjordBredbaand,
    Partner.NO_Signal,
    Partner.NO_Telefiber,
    Partner.NO_Tinn,
    Partner.NO_VarangerKraftFiber,
    Partner.NO_VesteraalskraftBredbaand,
    Partner.NO_VerdalKabelTV,
    Partner.NO_VikenFiber,
  ],
};

const internetSecuriyFilterVisibility = {
  filterType: FilterType.Whitelist,
  partners: [
    Partner.NO_Afiber,
    Partner.NO_Altibox_Training_Tenant,
    Partner.NO_Altifiber,
    Partner.NO_BergenFiber,
    Partner.NO_Bruse,
    Partner.NO_Drangedal,
    Partner.NO_EidsivaBredbaandAS,
    Partner.NO_Enivest,
    Partner.NO_FinnaasKraftlag,
    Partner.NO_FitjarKraftlag,
    Partner.NO_HaugalandKraft,
    Partner.NO_IstadFiber,
    Partner.NO_KleppEnergi,
    Partner.NO_KrageroeBredbaand,
    Partner.NO_LofotkraftBredbaand,
    Partner.NO_Lyse,
    Partner.NO_MidtTelemarkBreiband,
    Partner.NO_NotoddenEnergi,
    Partner.NO_NTE,
    Partner.NO_SandefjordBredbaand,
    Partner.NO_Signal,
    Partner.NO_Stroeyma,
    Partner.NO_Telefiber,
    Partner.NO_Tinn,
    Partner.NO_VarangerKraftFiber,
    Partner.NO_VesteraalskraftBredbaand,
    Partner.NO_VerdalKabelTV,
    Partner.NO_VikenFiber,
  ],
};

const internetSecurityCombinedPartners = Array.from(
  new Set([...internetSecurityVisibility.partners, ...internetSecuriyFilterVisibility.partners]),
);

export const pages: Pages = {
  aria: {
    url: `${config.app.ariaUrl}`,
    i18n: 'pages.aria.name',
    description: 'pages.aria.description',
    partnerVisibility: {
      partners: [], // Empty for now, while the business decides if they want to continue the Aria project or not
      filterType: FilterType.Whitelist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'aria',
    },
  },
  ariaTv: {
    url: `${config.app.ariaUrl}`,
    i18n: 'pages.ariaTv.name',
    description: 'pages.ariaTv.description',
    partnerVisibility: {
      partners: [], // Empty for now, while the business decides if they want to continue the Aria project or not
      filterType: FilterType.Whitelist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.TV,
      element_cta: 'ariaTv',
    },
  },
  enterpriseLineOverview: {
    url: `${window.origin}/min-bedrift/linjeoversikt`,
    i18n: 'pages.enterprise.linjeoversikt.name',
    requireOneOfServices: [
      Service.Internet,
      Service.OffnetInternet,
      Service.IpVPN,
      Service.OffnetIpVPN,
      Service.MobileAccess,
      Service.EthernetP2P,
      Service.OffnetEthernetP2P,
    ],
    customerType: CustomerType.BEDRIFT,
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'linjeoversikt',
    },
  },
  enterpriseLineStatus: {
    url: `${window.origin}/min-bedrift/linjestatus`,
    i18n: 'pages.enterprise.linjestatus.name',
    requireOneOfServices: [
      Service.Internet,
      Service.OffnetInternet,
      Service.IpVPN,
      Service.OffnetIpVPN,
      Service.MobileAccess,
      Service.EthernetP2P,
      Service.OffnetEthernetP2P,
    ],
    partnerVisibility: {
      partners: [Partner.NO_Lyse, Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    customerType: CustomerType.BEDRIFT,
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'linjestatus',
    },
  },
  enterpriseInternetSettingsOverview: {
    url: `${CONTEXT_PATH}/internett/innstillinger/oversikt`,
    i18n: 'pages.enterprise.internetSettings.name',
    requireOneOfServices: [Service.Internet, Service.FixedWirelessAccess, Service.ManagedWifi],
    customerType: CustomerType.BEDRIFT,
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'internett-innstillinger',
    },
  },
  enterpriseInternetSettingsNetworkSettings: {
    url: `${window.origin}/mine-sider/internett/min-hjemmesentral/`,
    i18n: 'pages.internetSettings.name',
    requireOneOfServices: [Service.Internet],
    partnerVisibility: {
      partners: [Partner.NO_Lyse, Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    customerType: CustomerType.BEDRIFT,
  },
  enterpriseWifiSettings: {
    url: `${CONTEXT_PATH}/internett/innstillinger/wifi`,
    i18n: 'pages.enterprise.wifiSettings.name',
    requireOneOfServices: [Service.ManagedWifi],
    customerType: CustomerType.BEDRIFT,
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'wifi',
    },
  },
  enterpriseGuestWifi: {
    url: `${CONTEXT_PATH}/internett/innstillinger/wifi/gjestenett`,
    i18n: 'pages.enterprise.guestWifi.name',
    requireOneOfServices: [Service.ManagedWifi],
    customerType: CustomerType.BEDRIFT,
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'gjestenett',
    },
  },
  enterpriseFirewall: {
    url: `${window.origin}/min-bedrift/brannmurinnstillinger`,
    i18n: 'pages.enterprise.firewall.name',
    requireOneOfServices: [Service.ManagedWifi],
    customerType: CustomerType.BEDRIFT,
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'brannmur',
    },
  },
  enterpriseDDOS: {
    url: `${window.origin}/min-bedrift/ddos-beskyttelse`,
    i18n: 'pages.enterprise.ddos.name',
    requireOneOfServices: [Service.Internet],
    customerType: CustomerType.BEDRIFT,
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'DDOS-beskyttelse',
    },
  },
  enterpriseExtendedSecurity: {
    url: `${window.origin}/min-bedrift/utvidet-sikkerhet`,
    i18n: 'pages.enterprise.extendedSecurity.name',
    requireOneOfServices: [Service.ManagedWifi],
    customerType: CustomerType.BEDRIFT,
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'utvidet_sikkerhet',
    },
  },
  enterpriseUpsale: {
    url: `https://www.altibox.no/bedrift/bredband/bredbandspakke/`,
    openInNewTab: true,
    i18n: 'pages.enterprise.upsale.name',
    requireNotHavingAnyOfServices: [Service.ManagedWifi],
    customerType: CustomerType.BEDRIFT,
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'fiberbredbåndspakke',
    },
  },
  enterpriseMyProducts: {
    url: `${window.origin}/min-bedrift/mine-produkter`,
    i18n: 'pages.enterprise.myProducts.name',
    customerType: CustomerType.BEDRIFT,
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'produkter',
    },
  },
  contactDetails: {
    url: `${CONTEXT_PATH}/konto/mitt-kundeforhold`,
    i18n: 'pages.contactDetails.name',
    partnerVisibility: {
      partners: [Partner.NO_EidsivaBredbaand],
      filterType: FilterType.Blacklist,
    },
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'mitt_kundeforhold',
    },
  },
  contactDetailsEdit: {
    url: `${CONTEXT_PATH}/konto/mitt-kundeforhold/endre-kontaktinformasjon`,
    i18n: 'pages.contactDetailsEdit.name',
    partnerVisibility: { ...commonNorwegianPartnerOnlyVisiblity },
    crm: CrmSystem.INFONOVA,
  },
  consentLyse: {
    url: config.partnerConfig.lyse.consentUrl || 'https://www.lyse.no/minesider/mitt-kundeforhold/samtykke',
    i18n: 'pages.consent.privacyAndConsents.name',
    customerType: CustomerType.PRIVAT,
    partnerVisibility: {
      partners: [Partner.NO_Lyse],
      filterType: FilterType.Whitelist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'samtykke_til_generell_markedsføring',
    },
  },
  foc: {
    url: 'https://tv.altibox.no/velg-innhold',
    i18n: 'pages.foc.name',
    requireOneOfServices: [Service.Tv],
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    customerType: CustomerType.PRIVAT,
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.TV,
      element_cta: 'velg_ditt_tv-innhold',
    },
  },
  inactiveMovies: {
    url: 'https://tv.altibox.no/film-og-serier/mitt-innhold',
    i18n: 'pages.inactive.movies.name',
    partnerVisibility: { ...commonNorwegianPartnerOnlyVisiblity },
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.TV,
      element_cta: 'se_dine_filmkjøp',
    },
  },
  invoice: {
    ...commonInvoiceRequirements,
    url: `${CONTEXT_PATH}/konto/faktura`,
    i18n: 'pages.invoice.name',
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'faktura',
    },
  },
  invoiceDetails: {
    ...commonInvoiceRequirements,
    url: `${CONTEXT_PATH}/konto/faktura/detaljer`,
    i18n: 'pages.invoiceDetails.name',
  },
  invoicePostpone: {
    ...commonInvoiceRequirements,
    url: `${CONTEXT_PATH}/konto/faktura/utsett-betalingsfrist/:invoiceId`,
    i18n: 'pages.invoice.invoicePostpone.name',
  },
  invoiceAvtaleGiro: {
    ...commonInvoiceRequirements,
    url: `${CONTEXT_PATH}/konto/faktura/avtalegiro`,
    i18n: 'pages.invoice.invoiceAvtaleGiro.name',
  },
  invoiceAvtaleGiroReceipt: {
    ...commonInvoiceRequirements,
    url: `${CONTEXT_PATH}/konto/faktura/avtalegiro/kvittering`,
    i18n: 'pages.invoice.invoiceAvtaleGiroReceipt.name',
  },
  ispDevices: {
    url: `${CONTEXT_PATH}/internett/nettverk/nettverksenheter/:deviceId`,
    i18n: 'pages.network.ispDevices.name',
    requireOneOfServices: [Service.Internet],
    partnerVisibility: { ...commonNorwegianPartnerOnlyVisiblity },
    crm: CrmSystem.INFONOVA,
  },
  modellAStore: {
    url: `${window.origin}/modella/store`,
    i18n: 'pages.modellAStore.name',
    requireOneOfServices: [Service.Tv],
    crm: CrmSystem.INFONOVA,
    partnerVisibility: { ...commonNorwegianPartnerOnlyVisiblity },
    customerType: CustomerType.PRIVAT,
    gtmDataLayer: {
      element_category: GtmElementCategory.TV,
      element_cta: 'bestill_ny_tv-dekoder',
    },
  },
  myDevices: {
    url: `${CONTEXT_PATH}/internett/nettverk/enheter/:deviceId`,
    i18n: 'pages.network.devices.name',
    requireOneOfServices: [Service.Internet],
    partnerVisibility: { ...commonNorwegianPartnerOnlyVisiblity },
    crm: CrmSystem.INFONOVA,
  },
  movieRentalHistory: {
    url: `${CONTEXT_PATH}/tv/filmleiehistorikk`,
    i18n: 'pages.movieRentalHistory.name',
    requireOneOfServices: [Service.Tv],
    customerType: CustomerType.PRIVAT,
    partnerVisibility: {
      partners: [Partner.NO_EidsivaBredbaand, Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.TV,
      element_cta: 'filmleiehistorikk',
    },
  },
  firewallCreateRule: {
    url: `${CONTEXT_PATH}/internett/innstillinger/portviderekobling/ny-regel`,
    i18n: 'pages.firewall.createNewFirewallRule.heading',
    requireOneOfServices: [Service.Internet],
    customerType: CustomerType.PRIVAT,
    crm: CrmSystem.INFONOVA,
  },
  firewallUpdateRule: {
    url: `${CONTEXT_PATH}/internett/innstillinger/portviderekobling/regel/:ruleId`,
    i18n: 'pages.firewall.buttons.updateFirewallRule',
    requireOneOfServices: [Service.Internet],
    customerType: CustomerType.PRIVAT,
    crm: CrmSystem.INFONOVA,
  },
  meshNetworkStore: {
    url: `${window.origin}/bestill/wifioveralt/order`,
    i18n: 'pages.meshNetworkStore.name',
    partnerVisibility: {
      partners: [...getDKPartners(), Partner.NO_EidsivaBredbaand, Partner.NO_VarangerKraftFiber],
      filterType: FilterType.Blacklist,
    },
    customerType: CustomerType.PRIVAT,
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'bestill_wifi_overalt',
    },
  },
  newsAndOffers: {
    url: `${CONTEXT_PATH}/konto/personvern-og-samtykker/nyheter-og-tilbud`,
    i18n: 'pages.newsAndOffers.name',
    crm: CrmSystem.INFONOVA,
    partnerVisibility: { ...commonNorwegianPartnerOnlyVisiblity },
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'kommunikasjon',
    },
  },
  relocation: {
    url: `${CONTEXT_PATH}/konto/melde-flytting/`,
    i18n: 'pages.relocationForm.name',
    crm: CrmSystem.INFONOVA,
    partnerVisibility: {
      ...commonNorwegianPartnerOnlyVisiblity,
      partners: [...commonNorwegianPartnerOnlyVisiblity.partners, Partner.NO_VikenFiber, Partner.NO_Lyse],
    },
    description: 'pages.relocationForm.description',
    customerType: CustomerType.PRIVAT,
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'melde_flytting',
    },
  },
  relocationFormVikenFiber: {
    url: 'https://vikenfiber.altibox.no/flytteskjema/',
    i18n: 'pages.relocationFormVikenFiber.name',
    partnerVisibility: {
      partners: [Partner.NO_VikenFiber],
      filterType: FilterType.Whitelist,
    },
    description: 'pages.relocationFormVikenFiber.description',
    customerType: CustomerType.PRIVAT,
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'melde_flytting',
    },
  },
  relocationFormLyse: {
    url: `${config.partnerConfig.lyse.relocationUrl}`,
    i18n: 'pages.relocationFormLyse.name',
    partnerVisibility: {
      partners: [Partner.NO_Lyse],
      filterType: FilterType.Whitelist,
    },
    description: 'pages.relocationFormLyse.description',
    customerType: CustomerType.PRIVAT,
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'melde_flytting',
    },
  },
  segmentPicker: {
    url: `${CONTEXT_PATH}/innvalg`,
    i18n: 'pages.customerSegmentSplash.name',
    partnerVisibility: { ...commonNorwegianPartnerOnlyVisiblity },
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'kundesegment_velger',
    },
  },
  tvStore: {
    url: `${window.origin}/bestill/kanaler/order`,
    i18n: 'pages.tvStore.name',
    requireOneOfServices: [Service.Tv],
    partnerVisibility: { ...commonNorwegianPartnerOnlyVisiblity },
    crm: CrmSystem.INFONOVA,
    customerType: CustomerType.PRIVAT,
    gtmDataLayer: {
      element_category: GtmElementCategory.TV,
      element_cta: 'kjøp_kanal-_og_poengpakker',
    },
  },
  voipOldForCustomerService: {
    url: `${window.origin}/mine-sider/ip-telefoni/innstillinger/`,
    i18n: 'pages.voip.csrName',
    requireOneOfServices: [Service.Voip],
    partnerVisibility: { ...commonNorwegianPartnerOnlyVisiblity },
    crm: CrmSystem.INFONOVA,
    csrLinkOnly: true,
  },

  voipSettingsOverview: {
    url: `${CONTEXT_PATH}/konto/telefoninnstillinger`,
    i18n: 'pages.voip.name',
    requireOneOfServices: [Service.Voip, Service.TaVoip],
    partnerVisibility: { ...commonNorwegianPartnerOnlyVisiblity },
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'innstillinger_hjemmetelefon',
    },
  },
  voipSettingsForwardingNewRule: {
    url: `${CONTEXT_PATH}/konto/telefoninnstillinger/viderekobling/ny-regel`,
    i18n: 'pages.voip.forwarding.rules.name',
    requireOneOfServices: [Service.Voip, Service.TaVoip],
    partnerVisibility: { ...commonNorwegianPartnerOnlyVisiblity },
    crm: CrmSystem.INFONOVA,
  },
  voipSettingsForwarding: {
    url: `${CONTEXT_PATH}/konto/telefoninnstillinger/viderekobling`,
    i18n: 'pages.voip.forwarding.name',
    requireOneOfServices: [Service.Voip, Service.TaVoip],
    partnerVisibility: { ...commonNorwegianPartnerOnlyVisiblity },
    crm: CrmSystem.INFONOVA,
  },
  voipSettingsRejectionNewRule: {
    url: `${CONTEXT_PATH}/konto/telefoninnstillinger/anrop/ny-regel`,
    i18n: 'pages.voip.rejection.rules.name',
    requireOneOfServices: [Service.Voip, Service.TaVoip],
    partnerVisibility: { ...commonNorwegianPartnerOnlyVisiblity },
    crm: CrmSystem.INFONOVA,
  },
  voipSettingsCalls: {
    url: `${CONTEXT_PATH}/konto/telefoninnstillinger/anrop`,
    i18n: 'pages.voip.calls.name',
    requireOneOfServices: [Service.Voip, Service.TaVoip],
    partnerVisibility: { ...commonNorwegianPartnerOnlyVisiblity },
    crm: CrmSystem.INFONOVA,
  },
  voipSettingsBarring: {
    url: `${CONTEXT_PATH}/konto/telefoninnstillinger/sperring`,
    i18n: 'pages.voip.barring.name',
    requireOneOfServices: [Service.Voip, Service.TaVoip],
    partnerVisibility: { ...commonNorwegianPartnerOnlyVisiblity },
    crm: CrmSystem.INFONOVA,
  },
  account: {
    i18n: 'pages.account.name',
    url: `${CONTEXT_PATH}/konto`,
  },
  authCallback: {
    i18n: 'pages.authCallback.name',
    url: `${CONTEXT_PATH}/auth/callback`,
  },
  changePin: {
    url: `${CONTEXT_PATH}/tv/endre-pin-koder`,
    i18n: 'pages.changePin.name',
    description: 'pages.changePin.description',
    requireOneOfServices: [Service.Tv],
    customerType: CustomerType.PRIVAT,
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.TV,
      element_cta: 'PIN-kode',
    },
  },
  hgwUserManual: {
    url: 'https://kundeservice.altibox.no/hc/no/articles/11740366686609-Hjemmesentraler-og-wifi-utstyr',
    i18n: 'pages.hgwUserManual.name',
    partnerVisibility: {
      partners: [Partner.NO_EidsivaBredbaand, Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    customerType: CustomerType.PRIVAT,
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'veiledning_til_hjemmesentral',
    },
  },
  email: {
    url: `${CONTEXT_PATH}/konto/e-postkonto`,
    i18n: 'pages.email.name',
    partnerVisibility: commonNorwegianPartnerOnlyVisiblity,
    customerType: CustomerType.PRIVAT,
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'Altibox_e-post',
    },
  },
  emailCreateNew: {
    url: `${CONTEXT_PATH}/konto/e-postkonto/opprett-ny`,
    i18n: 'pages.email.name',
    ...emailCommonRequirements,
  },
  emailChangePassword: {
    url: `${CONTEXT_PATH}/konto/e-postkonto/:emailAccountId/endre-passord`,
    i18n: 'pages.emailChangePassword.name',
    ...emailCommonRequirements,
  },
  emailChangeSenderName: {
    url: `${CONTEXT_PATH}/konto/e-postkonto/:emailAccountId/endre-navn-paa-avsender`,
    i18n: 'pages.emailChangeSenderName.name',
    ...emailCommonRequirements,
  },
  emailDeleteAccount: {
    url: `${CONTEXT_PATH}/konto/e-postkonto/:emailAccountId/slett-konto`,
    i18n: 'pages.emailDeleteAccount.name',
    ...emailCommonRequirements,
  },
  emailActivateAccount: {
    url: `${CONTEXT_PATH}/konto/e-postkonto/:emailAccountId/aktiver-konto`,
    i18n: 'pages.emailActivateAccount.name',
    ...emailCommonRequirements,
  },
  home: {
    url: `${CONTEXT_PATH}`,
    i18n: 'pages.home.name',
  },
  internet: {
    url: `${CONTEXT_PATH}/internett`,
    i18n: 'pages.internet.name',
  },
  internetSettingsWifiBridgeMode: {
    url: `${CONTEXT_PATH}/internett/innstillinger/wifi/bromodus-bekreftelse`,
    i18n: 'pages.network.advanced.settings.sections.bridge.confirmation',
    requireOneOfServices: [Service.Internet],
    partnerVisibility: commonNorwegianPartnerOnlyVisiblity,
    customerType: CustomerType.PRIVAT,
    crm: CrmSystem.INFONOVA,
  },
  internetSettingsWifiBand: {
    url: `${CONTEXT_PATH}/internett/innstillinger/wifi/avansert/kombiner/:wifiSettingId`,
    i18n: 'pages.network.advanced.wifi.bandHeader',
    requireOneOfServices: [Service.Internet],
    partnerVisibility: commonNorwegianPartnerOnlyVisiblity,
    customerType: CustomerType.PRIVAT,
    crm: CrmSystem.INFONOVA,
  },
  internetSettingsWifiAdvanced: {
    url: `${CONTEXT_PATH}/internett/innstillinger/wifi/avansert/:wifiSettingId`,
    i18n: 'pages.network.advanced.wifi.advanced.name',
    requireOneOfServices: [Service.Internet],
    customerType: CustomerType.PRIVAT,
    crm: CrmSystem.INFONOVA,
  },
  internetSettingsOverview: {
    url: `${CONTEXT_PATH}/internett/innstillinger/oversikt`,
    i18n: 'pages.internetSettings.name',
    requireOneOfServices: [Service.Internet],
    partnerVisibility: commonNorwegianPartnerOnlyVisiblity,
    customerType: CustomerType.PRIVAT,
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'internett-innstillinger',
    },
  },
  internetSettingsOverviewDk: {
    url: `${CONTEXT_PATH}/internett/innstillinger/oversikt`,
    i18n: 'pages.internetSettings.name',
    requireOneOfServices: [Service.Internet],
    partnerVisibility: commonDanishPartnerOnlyVisibility,
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'internett-innstillinger',
    },
  },
  internetSettingsNetworkSettings: {
    url: `${CONTEXT_PATH}/internett/innstillinger/nettverk`,
    i18n: 'pages.internetSettings.name',
    requireOneOfServices: [Service.Internet],
    partnerVisibility: commonNorwegianPartnerOnlyVisiblity,
    customerType: CustomerType.PRIVAT,
    crm: CrmSystem.INFONOVA,
  },
  internetSettingsNetworkSettingsDk: {
    url: `${CONTEXT_PATH}/internett/innstillinger/nettverk`,
    i18n: 'pages.internetSettings.name',
    requireOneOfServices: [Service.Internet],
    partnerVisibility: commonDanishPartnerOnlyVisibility,
    crm: CrmSystem.INFONOVA,
  },
  internetSettingsPortForwarding: {
    url: `${CONTEXT_PATH}/internett/innstillinger/portviderekobling`,
    i18n: 'pages.internetSettings.name',
    requireOneOfServices: [Service.Internet],
    partnerVisibility: commonNorwegianPartnerOnlyVisiblity,
    customerType: CustomerType.PRIVAT,
    crm: CrmSystem.INFONOVA,
  },
  internetSettingsPortForwardingDk: {
    url: `${CONTEXT_PATH}/internett/innstillinger/portviderekobling`,
    i18n: 'pages.internetSettings.name',
    requireOneOfServices: [Service.Internet],
    partnerVisibility: commonDanishPartnerOnlyVisibility,
    crm: CrmSystem.INFONOVA,
  },
  invoiceLegacy: {
    url: `${window.origin}/mine-sider/faktura-legacy`,
    i18n: 'pages.invoice.name',
    partnerVisibility: {
      partners: [Partner.NO_EidsivaBredbaand],
      filterType: FilterType.Whitelist,
    },
    customerType: CustomerType.PRIVAT,
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'faktura',
    },
  },
  invoiceLyse: {
    url: `${config.partnerConfig.lyse.invoiceUrl}`,
    i18n: 'pages.invoice.name',
    partnerVisibility: {
      partners: [Partner.NO_Lyse],
      filterType: FilterType.Whitelist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'faktura',
    },
  },
  logout: {
    url: `${CONTEXT_PATH}/logout`,
    i18n: 'pages.logout.name',
  },
  myProducts: {
    url: `${CONTEXT_PATH}/konto/produkter`,
    i18n: 'pages.myProducts.name',
    partnerVisibility: {
      partners: [Partner.NO_EidsivaBredbaand, Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    customerType: CustomerType.PRIVAT,
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'mine_produkter',
    },
  },
  myProductsDkEnterprise: {
    url: `${CONTEXT_PATH}/konto/produkter`,
    i18n: 'pages.myProducts.name',
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Whitelist,
    },
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'mine_produkter',
    },
  },
  privacyAndConsents: {
    url: `${CONTEXT_PATH}/konto/personvern-og-samtykker`,
    i18n: 'pages.consent.privacyAndConsents.name',
    description: 'pages.consent.privacyAndConsents.description',
    partnerVisibility: {
      partners: [Partner.NO_Lyse],
      filterType: FilterType.Blacklist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'samtykke_og_kommunikasjon',
    },
  },
  generalMarketing: {
    url: `${CONTEXT_PATH}/konto/personvern-og-samtykker/samtykker`,
    i18n: 'pages.consent.generalMarketing.name',
  },
  reservations: {
    url: `${CONTEXT_PATH}/konto/personvern-og-samtykker/mine-reservasjoner`,
    i18n: 'pages.consent.reservations.name',
  },
  provAuthCallback: {
    url: `${CONTEXT_PATH}/auth/prov/callback`,
    i18n: 'pages.provAuthCallback.name',
  },
  streamingServices: {
    url: `${CONTEXT_PATH}/tv/strommetjenester`,
    i18n: 'pages.streamingServices.name',
    requireOneOfServices: [Service.Tv],
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    customerType: CustomerType.PRIVAT,
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.TV,
      element_cta: 'strømmetjeneste',
    },
  },
  orderTrackerOpenNet: {
    url: config.stores.orderTrackerUrl || '',
    i18n: 'pages.orderTrackerOpenNet.name',
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Whitelist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'mine_bestillinger',
    },
  },
  streamingService: {
    url: `${CONTEXT_PATH}/tv/strommetjenester/:portalId`,
    i18n: 'pages.streamingService.name',
    requireOneOfServices: [Service.Tv],
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    crm: CrmSystem.INFONOVA,
  },
  telephone: {
    url: `${CONTEXT_PATH}/telefoni`,
    i18n: 'pages.telephone.name',
  },
  tv: {
    url: `${CONTEXT_PATH}/tv`,
    i18n: 'pages.tv.name',
  },
  tvStoreOpenNet: {
    url: `${window.origin}/mine-sider/bestil-tv-pakker/`,
    i18n: 'pages.tvStoreOpenNet.name',
    requireOneOfServices: [Service.Tv],
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Whitelist,
    },
    gtmDataLayer: {
      element_category: GtmElementCategory.TV,
      element_cta: 'bestill_TV-alternativ_og_streaming',
    },
  },
  users: {
    url: `${CONTEXT_PATH}/konto/brukere`,
    i18n: 'pages.users.name',
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'brukere',
    },
  },
  userEdit: {
    url: `${CONTEXT_PATH}/konto/brukere/rediger`,
    i18n: 'pages.userEdit.name',
    crm: CrmSystem.INFONOVA,
  },
  userCreate: {
    url: `${CONTEXT_PATH}/konto/brukere/opprett`,
    i18n: 'pages.userCreate.name',
    crm: CrmSystem.INFONOVA,
  },
  userDelete: {
    url: `${CONTEXT_PATH}/konto/brukere/slett/:id`,
    i18n: 'pages.userDelete.name',
    crm: CrmSystem.INFONOVA,
    customerType: CustomerType.PRIVAT,
  },
  voipUsage: {
    url: `${CONTEXT_PATH}/internett/ip-telefoni/forbruk/`,
    i18n: 'pages.voipUsage.name',
    partnerVisibility: {
      partners: [Partner.DK_AltiboxOpenNet],
      filterType: FilterType.Blacklist,
    },
    requireOneOfServices: [Service.Voip, Service.TaVoip],
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'telefoniforbruk',
    },
  },
  internetHjemApp: {
    url: `${CONTEXT_PATH}/internett/hjem-app`,
    i18n: 'pages.hjemApp.name',
    navigationBadge: 'features.navigation.badges.news',
    partnerVisibility: commonNorwegianPartnerOnlyVisiblity,
    requireOneOfServices: [Service.Internet],
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'hjem-app',
    },
  },
  internetSecurity: {
    url: `${CONTEXT_PATH}/internett/sikkerhet`,
    i18n: 'pages.security.name',
    navigationBadge: 'features.navigation.badges.news',
    partnerVisibility: {
      partners: internetSecurityCombinedPartners,
      filterType: FilterType.Whitelist,
    },
    requireOneOfServices: [Service.Internet],
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'sikkerhet',
    },
  },

  internetSecurityFilter: {
    url: `${CONTEXT_PATH}/internett/sikkerhet/nettvern`,
    i18n: 'pages.security.securityFilter.name',
    partnerVisibility: internetSecuriyFilterVisibility,
    requireOneOfServices: [Service.Internet],
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'nettvern',
    },
  },

  securityInsurancePackage: {
    url: `${CONTEXT_PATH}/internett/sikkerhet/trygg`,
    i18n: 'pages.security.insurance.name',
    partnerVisibility: internetSecurityVisibility,
    requireOneOfServices: [Service.Internet],
    crm: CrmSystem.INFONOVA,
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'trygg',
    },
  },

  // deliveryStatus is used as a catch-all for breadcrumbs and must be near bottom
  deliveryStatus: {
    url: `${CONTEXT_PATH}`,
    i18n: 'pages.deliveryStatus.name',
  },
  myHgw: {
    url: `${window.origin}/mine-sider/internett/min-hjemmesentral/`,
    i18n: 'pages.myHgw.name',
    requireOneOfServices: [Service.Internet],
    partnerVisibility: commonNorwegianPartnerOnlyVisiblity,
    customerType: CustomerType.BEDRIFT,
  },
  myUsageDK: {
    url: `${window.origin}/mine-sider/forbrugsoversigt`,
    i18n: 'pages.myUsageDK.name',
    partnerVisibility: commonDanishPartnerOnlyVisibility,
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'forbruk',
    },
  },
  meshNetworkStoreDK: {
    url: 'https://altibox.dk/google-nest-wifi',
    i18n: 'pages.meshNetworkStore.name',
    partnerVisibility: commonDanishPartnerOnlyVisibility,
    gtmDataLayer: {
      element_category: GtmElementCategory.INTERNET,
      element_cta: 'bestill_wifi_overalt',
    },
  },
  invoiceInformation: {
    url: `${window.origin}/mine-sider/hjaelp-til-din-faktura/`,
    i18n: 'pages.invoiceInformation.name',
    partnerVisibility: commonDanishPartnerOnlyVisibility,
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'sånn_leser_du_fakturaen',
    },
  },
  voipDK: {
    url: `${window.origin}/mine-sider/fastnet`,
    i18n: 'pages.voipDK.name',
    requireOneOfServices: [Service.Voip],
    partnerVisibility: commonDanishPartnerOnlyVisibility,
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'fastnet',
    },
  },
  orderDK: {
    url: `${window.origin}/mine-sider/bestil`,
    i18n: 'pages.orderDK.name',
    partnerVisibility: commonDanishPartnerOnlyVisibility,
    gtmDataLayer: {
      element_category: GtmElementCategory.ACCOUNT,
      element_cta: 'bestil',
    },
  },
};
